<template>
  <Breadcrumb />

  <!-- Step indicator -->
  <div class="mx-auto max-w-3xl pt-6 pb-2">
    <StepIndicator :steps="wizardSteps" :current-step="visibleStep" />
  </div>

  <!-- Wizard steps with transitions -->
  <Transition name="step-slide" mode="out-in">
    <!-- Step 1: Upload -->
    <div v-if="step === 'upload'" key="upload" class="mx-auto max-w-2xl py-8">
      <div class="text-center mb-8">
        <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
          <DocumentArrowUpIcon class="h-7 w-7 text-indigo-600" />
        </div>
        <h2 class="mt-4 text-lg font-semibold tracking-tight text-gray-900">
          {{ $gettext("Nouveau compromis de vente") }}
        </h2>
        <p class="mt-2 text-sm text-gray-500">
          {{ $gettext("Sélectionnez le PDF du compromis de vente. Le prix, l'acompte, la date et le compte bancaire seront extraits automatiquement.") }}
        </p>
      </div>

      <!-- Drop zone -->
      <div
        class="flex justify-center rounded-xl border-2 border-dashed px-6 py-12 transition-all duration-200"
        :class="isDragging
          ? 'border-indigo-400 bg-indigo-50/50'
          : 'border-gray-300 bg-gradient-to-b from-white to-slate-50 hover:border-gray-400'"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <div class="text-center">
          <DocumentIcon class="mx-auto h-12 w-12 text-gray-300" />
          <div class="mt-4 flex text-sm/6 text-gray-600">
            <label
              class="relative cursor-pointer rounded-md font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
            >
              <span>{{ $gettext("Choisir un fichier") }}</span>
              <input type="file" class="sr-only" accept=".pdf,application/pdf" @change="handleFileSelect" ref="fileInput" />
            </label>
            <p class="pl-1">{{ $gettext("ou glisser-déposer") }}</p>
          </div>
          <p class="text-xs/5 text-gray-400 mt-1">{{ $gettext("PDF jusqu'à 20 Mo") }}</p>
        </div>
      </div>

      <!-- Page selection -->
      <div class="mt-4 rounded-lg bg-gray-50 p-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">{{ $gettext("Premières pages") }}</label>
            <input
              type="number"
              v-model.number="firstPages"
              min="0"
              max="5"
              class="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">{{ $gettext("Dernières pages") }}</label>
            <input
              type="number"
              v-model.number="lastPages"
              min="0"
              max="5"
              class="mt-1 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
            />
          </div>
        </div>
        <p class="text-xs text-gray-500 mt-2">
          {{ $gettext("Les informations clés se trouvent généralement en début et fin de document. Max 10 pages au total.") }}
        </p>
      </div>

      <p v-if="fileError" class="mt-3 text-sm text-red-600">{{ fileError }}</p>

      <!-- Manual fill button -->
      <div class="mt-6 flex justify-center">
        <button
          type="button"
          class="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150"
          @click="skipToForm"
        >
          {{ $gettext("Remplir manuellement") }}
        </button>
      </div>
    </div>

    <!-- Step 2: Extracting -->
    <div v-else-if="step === 'extracting'" key="extracting">
      <ExtractionLoadingState
        :messages="extractionMessages"
        :subtitle="$gettext('Cela peut prendre jusqu\'à 30 secondes')"
      />
    </div>

    <!-- Step 3: Review -->
    <div v-else-if="step === 'review'" key="review" class="mx-auto max-w-3xl">
      <PurchaseAgreementExtractionReview
        ref="reviewComponent"
        :extraction-data="extractionData"
        :defaulted-fields="defaultedFields"
      />
      <div class="mt-6 flex justify-end gap-3 pb-8">
        <button
          type="button"
          class="rounded-md bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150"
          @click="resetToUpload"
        >
          {{ $gettext("Annuler") }}
        </button>
        <button
          type="button"
          class="rounded-md px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          :class="reviewComponent?.isValid ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-300 cursor-not-allowed'"
          :disabled="!reviewComponent?.isValid"
          @click="onConfirm"
        >
          {{ $gettext("Confirmer et pré-remplir") }}
        </button>
      </div>
    </div>

    <!-- Step 4: Error -->
    <div v-else-if="step === 'error'" key="error" class="mx-auto max-w-md py-16">
      <div class="rounded-xl bg-white shadow-sm ring-1 ring-gray-100 p-8 text-center">
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <ExclamationTriangleIcon class="h-8 w-8 text-red-400" />
        </div>
        <h3 class="mt-4 text-lg font-semibold text-gray-900">
          {{ $gettext("L'extraction a échoué") }}
        </h3>
        <p class="mt-2 text-sm text-gray-500">{{ errorMessage }}</p>
        <p class="mt-4 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3 text-left">
          {{ $gettext("Vérifiez que le PDF contient du texte sélectionnable (non scanné) et réessayez. Si le problème persiste, contactez le support.") }}
        </p>
        <div class="mt-6 flex justify-center gap-3">
          <button
            type="button"
            class="rounded-md bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150"
            @click="resetToUpload"
          >
            {{ $gettext("Retour à l'importation") }}
          </button>
          <button
            type="button"
            class="rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors duration-150"
            @click="retryWithSameFile"
          >
            {{ $gettext("Réessayer") }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Step 5: Form (outside transition wrapper) -->
  <Transition name="step-fade">
    <PurchaseAgreementForm
      v-if="step === 'form'"
      :purchaseAgreement="purchaseAgreement"
      :title="$gettext('Nouveau compromis de vente')"
      @submit="onSubmit"
      @cancel="onCancel"
    />
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useGettext } from "vue3-gettext";
import { DocumentArrowUpIcon, ExclamationTriangleIcon, DocumentIcon } from "@heroicons/vue/24/outline";
import { usePurchaseAgreementStore } from "@/stores/purchaseAgreement";
import { usePurchaseAgreementExtractionStore } from "@/stores/purchaseAgreementExtraction";
import { useExtractionSettingsStore } from "@/stores/extractionSettings";
import { useRouter, useRoute } from "vue-router";
import PurchaseAgreementForm from "@/components/purchase-agreements/PurchaseAgreementForm.vue";
import PurchaseAgreementExtractionReview from "@/components/purchase-agreement-extractions/PurchaseAgreementExtractionReview.vue";
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";
import StepIndicator from "@/components/ui/StepIndicator.vue";
import ExtractionLoadingState from "@/components/ui/ExtractionLoadingState.vue";

const { $gettext } = useGettext();
const router = useRouter();
const route = useRoute();
const saleListingAgreementId = route.query.saleListingAgreementId;
const purchaseOfferId = route.query.purchaseOfferId;

const purchaseAgreementStore = usePurchaseAgreementStore();
const extractionStore = usePurchaseAgreementExtractionStore();

// --- Deed extraction query-param prefilling (backward compat) ---
const prefillFinalPrice = route.query.prefillFinalPrice ? Number(route.query.prefillFinalPrice) : null;
const prefillSignatureDate = route.query.prefillSignatureDate || null;
const hasDeedPrefill = prefillFinalPrice || prefillSignatureDate;

// --- Wizard state ---
const step = ref(hasDeedPrefill ? "form" : "upload");
const purchaseAgreement = ref(
  hasDeedPrefill
    ? {
      attributes: {
        finalPrice: prefillFinalPrice,
        signatureDate: prefillSignatureDate,
        deposit: null,
        balance: prefillFinalPrice,
        buyerAccountNumber: null
      },
      relationships: {
        buyerAccountCountry: { data: { id: null } },
        purchaseAgreementDocuments: { data: [] }
      }
    }
    : purchaseAgreementStore.empty
);
const isDragging = ref(false);
const fileError = ref(null);
const errorMessage = ref(null);
const extractionData = ref(null);
const reviewComponent = ref(null);
const fileInput = ref(null);
const extractionSettingsStore = useExtractionSettingsStore();
const defaultedFields = ref([]);
const firstPages = ref(3);
const lastPages = ref(2);

// The 3/2 above is only the fallback while the organization's settings load.
const applyConfiguredPages = () => {
  const pages = extractionSettingsStore.pagesFor("purchase_agreement");
  if (!pages) return;

  firstPages.value = pages.firstPages;
  lastPages.value = pages.lastPages;
};

onMounted(async () => {
  await extractionSettingsStore.ensureLoaded();
  applyConfiguredPages();
});
const uploadedFile = ref(null);

const MAX_FILE_SIZE = 20 * 1024 * 1024;

// --- Step indicator ---
const wizardSteps = computed(() => [
  { key: "upload", label: $gettext("Importation") },
  { key: "extraction", label: $gettext("Extraction") },
  { key: "validation", label: $gettext("Validation") },
  { key: "form", label: $gettext("Formulaire") },
]);

const visibleStep = computed(() => {
  const map = {
    upload: "upload",
    extracting: "extraction",
    review: "validation",
    error: "extraction",
    form: "form",
  };
  return map[step.value] || "upload";
});

// --- Loading messages ---
const extractionMessages = computed(() => [
  $gettext("Lecture du document..."),
  $gettext("Analyse du compromis de vente..."),
  $gettext("Extraction du prix et de l'acompte..."),
  $gettext("Recherche du compte bancaire..."),
]);

// --- File handling ---
const validateFile = (file) => {
  if (!file) return false;
  if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
    fileError.value = "Le fichier doit être un PDF";
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    fileError.value = "Le fichier est trop volumineux (max 20 Mo)";
    return false;
  }
  const totalPages = (firstPages.value || 0) + (lastPages.value || 0);
  if (totalPages < 1) {
    fileError.value = "Au moins une page doit être sélectionnée";
    return false;
  }
  if (totalPages > 10) {
    fileError.value = "Maximum 10 pages au total";
    return false;
  }
  fileError.value = null;
  return true;
};

const startExtraction = async (file) => {
  uploadedFile.value = file;
  step.value = "extracting";
  await extractionStore.extract(
    file,
    firstPages.value,
    lastPages.value,
    (data, meta) => {
      extractionData.value = data;
      defaultedFields.value = meta?.defaultedFields ?? [];
      step.value = "review";
    },
    (errors) => {
      const isFileTooLarge = errors?.value && Object.values(errors.value).some(msg => msg?.includes?.("taille maximale"));
      errorMessage.value = isFileTooLarge
        ? $gettext("Le fichier dépasse la taille maximale autorisée (10 Mo). Veuillez utiliser un fichier plus petit.")
        : $gettext("Une erreur est survenue lors de l'extraction. Veuillez réessayer.");
      step.value = "error";
    }
  );
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (validateFile(file)) {
    startExtraction(file);
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (validateFile(file)) {
    startExtraction(file);
  }
};

// --- Confirm (no backend apply — just map to form) ---
const onConfirm = () => {
  const reviewedData = reviewComponent.value?.getReviewedData();
  if (!reviewedData) return;

  const agreementDocs = uploadedFile.value
    ? [{ attributes: { file: uploadedFile.value, categoryName: "signedPurchaseAgreement" } }]
    : [];

  purchaseAgreement.value = {
    attributes: {
      signatureDate: reviewedData.signatureDate,
      finalPrice: reviewedData.finalPrice,
      deposit: reviewedData.depositAmount,
      balance: (reviewedData.finalPrice || 0) - (reviewedData.depositAmount || 0),
      buyerAccountNumber: reviewedData.buyerAccountNumber,
    },
    relationships: {
      buyerAccountCountry: { data: { id: reviewedData.buyerAccountCountryId } },
      purchaseAgreementDocuments: { data: agreementDocs },
    },
  };

  step.value = "form";
};

// --- Skip to manual form ---
const skipToForm = () => {
  purchaseAgreement.value = purchaseAgreementStore.empty;
  step.value = "form";
};

// --- Navigation ---
const resetToUpload = () => {
  step.value = "upload";
  fileError.value = null;
  errorMessage.value = null;
  extractionData.value = null;
  uploadedFile.value = null;
  defaultedFields.value = [];
  applyConfiguredPages();
  extractionStore.reset();
};

const retryWithSameFile = () => {
  if (uploadedFile.value) {
    startExtraction(uploadedFile.value);
  } else {
    resetToUpload();
  }
};

const onSubmit = (payload, actions) => {
  purchaseAgreementStore.save(payload, saleListingAgreementId, purchaseOfferId, () => {
    router.push({ name: "sale-listing-agreement", params: { id: saleListingAgreementId } });
  }, (errors) => {
    actions.setErrors(errors.value);
  });
};

const onCancel = () => {
  router.back();
};
</script>

<style scoped>
.step-slide-enter-active {
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}
.step-slide-leave-active {
  transition: opacity 0.15s ease-in, transform 0.15s ease-in;
}
.step-slide-enter-from {
  opacity: 0;
  transform: translateX(12px);
}
.step-slide-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}
.step-fade-enter-active {
  transition: opacity 0.3s ease-out;
}
.step-fade-leave-active {
  transition: opacity 0.15s ease-in;
}
.step-fade-enter-from,
.step-fade-leave-to {
  opacity: 0;
}
</style>
